/*
 * Figma `48:32` — Data Table Row (Default / Hover / Selected), and the table that holds it.
 *
 * Generic over the row type so a queue of orders and a log of vendor replies are the same component
 * rather than two tables that drift apart. What it owns is presentation and the contract the brief
 * requires of every table — sort affordance, selection, pagination, and the four data states drawn
 * from one place. What it does not own is fetching, filtering or sorting *data*: those stay with
 * the screen, because most of these lists are paged and sorted by the server and a table that
 * quietly sorted the current page would be showing a different answer than the one asked for.
 *
 * The two empties are separate props on purpose. `empty` is "there is nothing"; `filteredEmpty` is
 * "your filter matched nothing" — different sentences, and only the second one has a way out.
 */
import type { ReactNode } from 'react';
import { FeedbackState, LoadingRows } from './FeedbackState';
import { Button } from './Button';
import { useIsPhone } from './useMediaQuery';

export type SortDirection = 'asc' | 'desc';

export interface Column<T> {
  key: string;
  header: ReactNode;
  /** Omit to make the column unsortable — most are. */
  sortable?: boolean;
  /** Right-align figures; `wrap` lets long prose columns breathe instead of forcing a scroll. */
  align?: 'start' | 'end';
  width?: string;
  cell: (row: T) => ReactNode;
}

export interface DataTableProps<T> {
  /** Names the table for assistive tech — required, because "table" on its own says nothing. */
  label: string;
  columns: readonly Column<T>[];
  rows: readonly T[];
  rowKey: (row: T) => string;

  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;

  /** Whether any filter or search term is currently narrowing the list. Picks which empty to draw. */
  filtered?: boolean;
  onClearFilters?: () => void;
  emptyTitle?: string;
  emptyText?: ReactNode;

  sort?: { key: string; direction: SortDirection } | null;
  onSortChange?: (key: string, direction: SortDirection) => void;

  /** Selection is opt-in: passing `selected` turns the checkbox column on. */
  selected?: ReadonlySet<string>;
  onSelectedChange?: (next: Set<string>) => void;

  onRowClick?: (row: T) => void;
  /** The row currently open in a detail drawer, marked as selected in the Figma sense. */
  activeRowKey?: string | null;

  /**
   * The same row as a record card, for phone width — nodes `127:67`, `127:143`, `127:218`.
   *
   * A table narrower than its columns is a table nobody reads; the frozen design turns each row
   * into a card instead. Give the card the two or three fields that decide something, not every
   * column: the card is a summary that opens the row, not the row squeezed.
   */
  card?: (row: T) => ReactNode;

  footer?: ReactNode;
}

export function DataTable<T>({
  label, columns, rows, rowKey,
  loading = false, error = null, onRetry,
  filtered = false, onClearFilters, emptyTitle, emptyText,
  sort = null, onSortChange,
  selected, onSelectedChange,
  onRowClick, activeRowKey = null,
  card,
  footer,
}: DataTableProps<T>) {
  const phone = useIsPhone();

  if (loading) return <LoadingRows rows={6} label={`กำลังโหลด${label}`} />;

  if (error) {
    return (
      <FeedbackState state="error" title={`โหลด${label}ไม่สำเร็จ`} onRetry={onRetry}>
        {error}
      </FeedbackState>
    );
  }

  if (rows.length === 0) {
    return filtered ? (
      <FeedbackState
        state="filteredEmpty"
        title="ไม่มีรายการที่ตรงกับตัวกรอง"
        onClearFilters={onClearFilters}
      >
        ข้อมูลยังอยู่ครบ — ตัวกรองหรือคำค้นปัจจุบันไม่ตรงกับรายการใด ลองล้างตัวกรองเพื่อดูทั้งหมด
      </FeedbackState>
    ) : (
      <FeedbackState state="empty" title={emptyTitle ?? `ยังไม่มี${label}`}>
        {emptyText}
      </FeedbackState>
    );
  }

  const selectable = selected !== undefined && onSelectedChange !== undefined;
  const allKeys = rows.map(rowKey);
  const allSelected = selectable && allKeys.length > 0 && allKeys.every((k) => selected!.has(k));
  const someSelected = selectable && allKeys.some((k) => selected!.has(k));

  const toggle = (key: string) => {
    const next = new Set(selected!);
    if (next.has(key)) next.delete(key); else next.add(key);
    onSelectedChange!(next);
  };

  if (phone && card) {
    return (
      <>
        <ul className="jt-record-list" aria-label={label}>
          {rows.map((row) => {
            const key = rowKey(row);
            const isSelected = (selectable && selected!.has(key)) || activeRowKey === key;
            return (
              <li key={key} className="jt-record" data-selected={isSelected || undefined}>
                {selectable && (
                  <input
                    type="checkbox"
                    className="jt-record-check"
                    aria-label={`เลือกแถว ${key}`}
                    checked={selected!.has(key)}
                    onChange={() => toggle(key)}
                  />
                )}
                {onRowClick ? (
                  <button type="button" className="jt-record-open" onClick={() => onRowClick(row)}>
                    {card(row)}
                  </button>
                ) : (
                  <div className="jt-record-body">{card(row)}</div>
                )}
              </li>
            );
          })}
        </ul>
        {footer}
      </>
    );
  }

  return (
    <>
      {/* Wide tables scroll deliberately inside their own container — the page body never does. */}
      <div className="jt-table-scroll">
        <table className="jt-table">
          <caption className="jt-sr-only">{label}</caption>
          <thead>
            <tr>
              {selectable && (
                <th scope="col" style={{ width: '44px' }}>
                  <input
                    type="checkbox"
                    aria-label={allSelected ? `ยกเลิกเลือกทั้งหมดใน${label}` : `เลือกทั้งหมดใน${label}`}
                    checked={allSelected}
                    ref={(el) => { if (el) el.indeterminate = !allSelected && someSelected; }}
                    onChange={() => onSelectedChange!(allSelected ? new Set() : new Set(allKeys))}
                  />
                </th>
              )}
              {columns.map((col) => {
                const active = sort?.key === col.key;
                const direction: SortDirection | 'none' = active ? sort!.direction : 'none';
                return (
                  <th
                    key={col.key}
                    scope="col"
                    style={{ width: col.width, textAlign: col.align === 'end' ? 'end' : undefined }}
                    /* The header's own aria-sort is the source the arrow is drawn from, so the
                     * glyph and the announcement cannot disagree. */
                    aria-sort={!col.sortable ? undefined : active ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                  >
                    {col.sortable && onSortChange ? (
                      <button
                        type="button"
                        className="jt-th-sort"
                        data-sort={direction}
                        onClick={() => onSortChange(col.key, active && sort!.direction === 'asc' ? 'desc' : 'asc')}
                      >
                        {col.header}
                      </button>
                    ) : col.header}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const key = rowKey(row);
              const isSelected = (selectable && selected!.has(key)) || activeRowKey === key;
              return (
                <tr
                  key={key}
                  data-selected={isSelected || undefined}
                  aria-selected={selectable ? selected!.has(key) : undefined}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  style={onRowClick ? { cursor: 'pointer' } : undefined}
                >
                  {selectable && (
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label={`เลือกแถว ${key}`}
                        checked={selected!.has(key)}
                        onChange={() => toggle(key)}
                      />
                    </td>
                  )}
                  {columns.map((col) => (
                    <td key={col.key} style={{ textAlign: col.align === 'end' ? 'end' : undefined }}>
                      {col.cell(row)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {footer}
    </>
  );
}

/**
 * Pagination, or the "load more" the paged endpoints actually support.
 *
 * Several of these lists are cursor-paged server-side with no total, so a numbered pager would be
 * inventing a page count. Where a total is known it is stated; where it is not, the control says
 * what it will do rather than pretending to know how much is left.
 */
export function TableFooter({ shown, total, onLoadMore, loadingMore = false }: {
  shown: number;
  total?: number;
  onLoadMore?: () => void;
  loadingMore?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 'var(--jt-space-3)', padding: 'var(--jt-space-3) 0', flexWrap: 'wrap',
      }}
    >
      <div className="jt-field-hint" aria-live="polite">
        {total === undefined ? `แสดง ${shown} รายการ` : `แสดง ${shown} จาก ${total} รายการ`}
      </div>
      {onLoadMore && (
        <button type="button" className="jt-btn" data-style="secondary" data-size="sm" disabled={loadingMore} onClick={onLoadMore}>
          {loadingMore ? 'กำลังโหลด…' : 'โหลดเพิ่ม'}
        </button>
      )}
    </div>
  );
}

/**
 * The bulk-action rail — what the selection can have done to it, once there is one.
 *
 * It appears only when something is selected, states how many, and offers a way out of the
 * selection that is not "untick them one at a time". Destructive bulk actions stay `ConfirmDialog`'s
 * job: this rail opens them, it does not perform them.
 *
 * The count is a live region because selection changes from the header checkbox too, where the
 * thing that moved is nowhere near the thing that changed.
 */
export function BulkActionBar({ count, onClear, children, label }: {
  count: number;
  onClear: () => void;
  /** The actions. Buttons, in consequence order, with the destructive one last. */
  children: ReactNode;
  /** What is selected, e.g. "ออร์เดอร์". */
  label: string;
}) {
  if (count === 0) return null;
  return (
    <div className="jt-bulk" role="region" aria-label={`การทำงานกับ${label}ที่เลือก`}>
      <p className="jt-bulk-count" aria-live="polite">
        เลือก{label}แล้ว {count.toLocaleString('th-TH')} รายการ
      </p>
      <div className="jt-bulk-actions">
        {children}
        <Button size="sm" variant="ghost" onClick={onClear}>ล้างการเลือก</Button>
      </div>
    </div>
  );
}
